import SomethingWrong from '@modules/error/component/something-wrong/SomethingWrong';
import ErrorPageWrapper from '@modules/error/component/error-page-wrapper/ErrorPageWrapper';
import DevErrorPage from '@modules/error/component/dev-error-page/DevErrorPage';
import { useLogger } from '@archipro-website/logger/client';
import { useDebug } from '@modules/error/context/DebugContext';
import { useRootData } from '@modules/root/hook/use-root-data';
import { useMount } from 'ahooks';
import type { ServerError } from '@apollo/client';
import { isApolloError } from '@apollo/client';
import MatrixRain from '@modules/error/component/matrix-rain/matrix-rain';
import NetworkError from '@modules/error/component/network-error/NetworkError';
import { isFirefox } from '~/utils/network-helper';
import {
    tryReloadOnStaleClient,
    isStaleClientError,
} from '@modules/error/util/stale-client-reload';

const isServerError = (error: Error | null): error is ServerError => {
    if (!error) {
        return false;
    }
    if (typeof error !== 'object') {
        return false;
    }
    return error.name === 'ServerError';
};

const isNetworkError = (error: Error): boolean =>
    // Chrome/Firefox emit `Failed to fetch`; WebKit (Safari/iOS) emits
    // `Load failed` for the same aborted-loader fetch. Treat both as noise.
    error.message === 'Failed to fetch' || error.message === 'Load failed';

const isFirefoxNetworkError = (error: Error): boolean =>
    error.message === 'NetworkError when attempting to fetch resource.' &&
    isFirefox();

/**
 * What kind of error reached the boundary — drives both logging and which UI we
 * render, so it's classified once here instead of being re-derived inline:
 *  - `deployment`    a new build shipped mid-session (friendly MatrixRain splash)
 *  - `fetch-abort`   `Failed to fetch` / `Load failed` (WebKit) — React Router
 *                    aborting an in-flight loader on navigation; noise,
 *                    suppressed from logging
 *  - `firefox-abort` the Firefox-only prefetch-abort variant; same noise
 *  - `stale-client`  a chunk/module fetch or single-fetch `.data` decode failed
 *                    after a deploy shipped while the tab was open.
 *                    `tryReloadOnStaleClient` reloads to recover, so we keep the
 *                    friendly page (never the dev page) if the reload is
 *                    cooldown-blocked.
 *  - `generic`       a real fault (incl. Apollo/GraphQL) — the only kind that
 *                    gets the detailed dev error page outside Production
 */
type ErrorKind =
    | 'deployment'
    | 'fetch-abort'
    | 'firefox-abort'
    | 'stale-client'
    | 'generic';

const classifyError = (error: Error): ErrorKind => {
    if (error.message === 'Deployment in progress') return 'deployment';
    if (isNetworkError(error)) return 'fetch-abort';
    if (isFirefoxNetworkError(error)) return 'firefox-abort';
    if (isStaleClientError(error)) return 'stale-client';
    return 'generic';
};

const isNavigationAbort = (kind: ErrorKind): boolean =>
    kind === 'fetch-abort' || kind === 'firefox-abort';

const ErrorPage = ({ error }: { error: Error }) => {
    const logger = useLogger();
    const { add } = useDebug();
    const rootData = useRootData();
    const kind = classifyError(error);

    let message = error.message;
    if (isApolloError(error)) {
        message = `Graphql Error occurred`;
        const { networkError } = error;
        if (isServerError(networkError) && networkError.statusCode === 503) {
            message = `Graphql service is unavailable`;
        }
    }
    if (typeof document === 'undefined') {
        add({ message, stack: error.stack });
    }

    useMount(() => {
        // Navigation aborts (React Router cancelling an in-flight loader) are
        // noise, not faults. Keep a console.warn so a genuine outage that maps
        // to the same shape is still visible in devtools.
        if (isNavigationAbort(kind)) {
            console.warn('[ErrorPage] navigation-abort (suppressed)', error);
            return;
        }
        // Deploy-race recovery: a chunk or single-fetch payload failed because a
        // new build shipped while this tab was open. Reload once (loop-guarded)
        // to pick up the new build instead of stranding the user here, skipping
        // crit logging since this is an expected, self-healed race.
        if (tryReloadOnStaleClient(error)) {
            return;
        }
        logger.crit(message, error);
        add({ message, stack: error.stack });
    });

    // A `generic` fault outside Production gets the detailed dev error page (full
    // stack) to speed up debugging; everything else (and all of Production) gets
    // the friendly pages. When the root loader itself failed `rootData` is
    // unavailable, so fall back to the build-time flag — false in a Production
    // build, keeping the generic page safe (no stack leak) by default.
    const stage = rootData?.env?.stage;
    const isProdEnv = stage ? stage === 'Production' : !import.meta.env.DEV;

    if (kind === 'fetch-abort') {
        return (
            <ErrorPageWrapper>
                <NetworkError />
            </ErrorPageWrapper>
        );
    }
    if (kind === 'deployment') {
        return (
            <ErrorPageWrapper>
                <MatrixRain />
                <SomethingWrong />
            </ErrorPageWrapper>
        );
    }
    if (kind === 'generic' && !isProdEnv) {
        return <DevErrorPage error={error} errorInfo={null} />;
    }
    return (
        <ErrorPageWrapper>
            <SomethingWrong />
        </ErrorPageWrapper>
    );
};

export default ErrorPage;
